import * as React from 'react';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import { TRANSPORT_ROUTES } from 'routes/transport-routes';
import { useTranslation } from 'hooks';
import { useFetchTransportRoutes } from 'hooks/api/tranpsort-routes';
import { useTransportRouteSearchParams } from 'hooks/store/use-transport-route-search-params';
import { SettingLayout } from 'layouts/SettingLayout';
import { MainContainer } from 'components/shared';
import { DataTable2 } from 'components/ui/DataTable2';
import { PageHeaderForFilters } from 'components/ui/page-header-for-filters';

import { useTabOptions } from '../hooks';
import { useTransportRouteColumns } from './hooks';
import { TransportRouteFilters } from './components';
import { translations } from './translations';

export const ListTransportRoutesPage = () => {
  const history = useHistory();
  const tabOptions = useTabOptions();
  const { getTranslation: t } = useTranslation(translations);

  const { fetchTransportRoutes } = useFetchTransportRoutes();
  const {
    filters,
    paginationControl,
    updateFilters,
    updatePaginationControl,
    clearState,
  } = useTransportRouteSearchParams({ persisted: true });

  const transportRoutesQuery = useQuery({
    queryKey: ['fetch-transport-routes', filters, paginationControl],
    queryFn: () => {
      return fetchTransportRoutes({
        filters,
        pagination: paginationControl,
      });
    },
  });

  const handleFiltersSubmission = React.useCallback((updatedFilters: typeof filters) => {
    updatePaginationControl({ page: 1 });
    updateFilters(updatedFilters);
  }, [updatePaginationControl, updateFilters]);

  const handleDatatablePagination = React.useCallback(
    (payload: typeof paginationControl) => {
      updatePaginationControl(payload);
    },
    [updatePaginationControl],
  );

  const handleNavigateToRoute = React.useCallback((routeId: number) => {
    history.push(TRANSPORT_ROUTES.updateRoute(routeId).url);
  }, [history]);

  const columns = useTransportRouteColumns({
    onClickNavigateToDetails: handleNavigateToRoute,
  });

  return (
    <MainContainer>
      <SettingLayout
        page={TRANSPORT_ROUTES.listRoutes().path}
        options={tabOptions}
      >
        <PageHeaderForFilters headerTitle={
          <div className={'w-full font-GilroySemibold'}>{t('filtersTitle')}</div>
        } className={'mb-6'}>
          <TransportRouteFilters
            onSubmitFilters={handleFiltersSubmission}
            onResetFilters={clearState}
          />
        </PageHeaderForFilters>
        <DataTable2
          columns={columns}
          data={transportRoutesQuery?.data?.routes ?? []}
          manualPagination
          isLoading={transportRoutesQuery.isLoading}
          onPaginationChange={handleDatatablePagination}
          pagination={{
            ...transportRoutesQuery?.data?.pagination,
            page: paginationControl.page,
            perPage: paginationControl.limit,
          }}
        />
      </SettingLayout>
    </MainContainer>
  );
};